import React, { useEffect } from "react";
import {
  CustomCategories,
  Service,
  ServicesResponse,
  SubCategory,
  fetchTimmaData,
  injectPhotos,
} from "@/lib/timma_salon_data";
// import timmafotos from "@/lib/timmafotos.json";

import {
  getFreeTimes,
  bookTime,
  getWorkerData,
  FreeTimesRequestPayload,
  FreeTimesResponse,
  BookTimeRequestPayload,
  BookTimeResponse,
  WorkerResponse,
  Worker,
} from "@/lib/timma_booking";

const getServiceName = (service: Service, lang: string) => {
  switch (lang) {
    case "fi":
      return service.nameFi;
    case "en":
      return service.nameEn;
    case "es":
      return service.nameEs;
    default:
      return service.nameFi;
  }
};

const getServiceDescription = (service: Service, lang: string) => {
  switch (lang) {
    case "fi":
      return service.descriptionFi;
    case "en":
      return service.descriptionEn;
    case "es":
      return service.descriptionEs;
    default:
      return service.descriptionFi;
  }
};

const determineCategoryName = (row: SubCategory, data: ServicesResponse) => {
  const customCategories = data.publicSettings.customCategories;
  let val = {
    nameFi: row.nameFi,
    nameEn: row.nameEn,
    nameEs: row.nameEs,
  };
  Object.keys(customCategories).map((v) => {
    if (v === (String(row.subCategoryId) as keyof CustomCategories)) {
      val.nameFi = customCategories[v].nameFi;
      val.nameEn = customCategories[v].nameEn;
      val.nameEs = customCategories[v].nameEs;
      return;
    }
  });
  return val;
};

const makeCategories = (data: ServicesResponse) => {
  let newData = data?.subCategories?.map((c) => {
    let newServices = c.services.map((s) => {
      const newService: Service = {
        ...s,
        descriptionEn: s.descriptionEn,
        descriptionFi: s.descriptionFi,
        descriptionEs: s.descriptionEs,
        nameFi: s.nameFi,
        nameEn: s.nameEn,
        nameEs: s.nameEs,
        price: s.price,
        clientDuration: s.clientDuration,
        duration: s.duration,
      };
      return newService;
    });
    const customCategoryName = determineCategoryName(c, data);
    let newSubCategory: SubCategory = {
      ...c,
      services: newServices,
      nameFi: customCategoryName.nameFi,
      nameEn: customCategoryName.nameEn,
      nameEs: customCategoryName.nameEs,
    };
    return newSubCategory;
  });
  return newData;
};

export type TimmaContextData = {
  loading: boolean;
  error: Error | null;
  data: ServicesResponse | null;
  refetch: () => Promise<void>;
  injectPhotos: (servicesResponse: ServicesResponse) => ServicesResponse;
  getFreeTimes: (
    body: FreeTimesRequestPayload
  ) => Promise<FreeTimesResponse | undefined>;
  bookTime: (
    body: BookTimeRequestPayload
  ) => Promise<BookTimeResponse | undefined>;
  getWorkerData: () => Promise<WorkerResponse | undefined>;
  workers: WorkerResponse | undefined;
  subCategories: SubCategory[];
};

export const TimmaContext = React.createContext<TimmaContextData>(
  {} as TimmaContextData
);

export const useTimmaContext = (): TimmaContextData => {
  const context = React.useContext(TimmaContext);
  if (!context) {
    throw new Error("useTimmaContext must be used within a TimmaProvider");
  }
  return context;
};

export const TimmaProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [loading, _setLoading] = React.useState<boolean>(false);
  const [error, _setError] = React.useState<Error | null>(null);
  const [data, _setData] = React.useState<ServicesResponse | null>(null);
  const [workers, _setWorkers] = React.useState<WorkerResponse>();

  const [subCategories, _setSubCategories] = React.useState<SubCategory[]>([]);

  const getData = async () => {
    _setLoading(true);
    const timmares = fetchTimmaData(
      (jsonData) => {
        const d = injectPhotos(jsonData);
        _setData(d);
      },
      (err) => {
        _setError(err);
      }
    );
    const workerData = async () => {
      const workerResponse = await getWorkerData();
      if (workerResponse) {
        _setWorkers(workerResponse);
      }
    };
    await Promise.allSettled([timmares, workerData()]);
  };

  const refetch = async () => {
    await getData();
  };
  React.useEffect(() => {
    if (data) {
      const categories = makeCategories(data);
      _setSubCategories(categories);
      _setLoading(false);
    }
  }, [data]);
  React.useEffect(() => {
    if (!data) {
      refetch();
    }
  }, []);

  // const printTimmaPhotos = () => {
  //   type TimmaServices = {
  //     subCategories: SubCategory[];
  //   };

  //   type Photos = {
  //     [key: string]: string[];
  //   };

  //   type NewPhotos = {
  //     [key: string]: {
  //       nameFi: string;
  //       nameEn: string;
  //       nameEs: string;
  //       photoFileNames: string[];
  //     };
  //   };
  //   let newPhotos = {} as NewPhotos;

  //   const photos: Photos = timmafotos;

  //   const serviceIDs = subCategories
  //     .flatMap((c) => c.services)
  //     .forEach((service) => {
  //       if (photos[service.serviceId]) {
  //         newPhotos[service.serviceId] = {
  //           nameFi: getServiceName(service, "fi"),
  //           nameEn: getServiceName(service, "en"),
  //           nameEs: getServiceName(service, "es"),
  //           photoFileNames: photos[service.serviceId],
  //         };
  //       } else {
  //         newPhotos[service.serviceId] = {
  //           nameFi: getServiceName(service, "fi"),
  //           nameEn: getServiceName(service, "en"),
  //           nameEs: getServiceName(service, "es"),
  //           photoFileNames: [],
  //         };
  //       }
  //     });

  //   console.log(newPhotos);
  // };

  // useEffect(() => {
  //   if (subCategories.length > 0) {
  //     printTimmaPhotos();
  //   }
  // }, [subCategories]);

  return (
    <TimmaContext.Provider
      value={{
        loading,
        error,
        data,
        refetch,
        injectPhotos,
        getFreeTimes,
        bookTime,
        getWorkerData,
        workers,
        subCategories,
      }}
    >
      {children}
    </TimmaContext.Provider>
  );
};
